import en from '@/locale/en.json';
import fr from '@/locale/fr.json';
import {
  BUS_TOPIC_GLOBALLANGUAGE,
  BUS_TOPIC_SGCONNECT_USERINFO,
  getWidgetBus,
} from '@/sgwt/widgets.ts';
import { isDefined } from '@/utils/assertions';
import { type JSX, Fragment, useEffect, useState } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';

type SupportedLocale = 'en' | 'fr';

export const locales: Record<SupportedLocale, Record<string, string>> = {
  en,
  fr: { ...en, ...fr },
};

export const IntlProvider = ({ children }: { children: JSX.Element }) => {
  const [locale, setLocale] = useState<SupportedLocale>('fr');

  useEffect(() => {
    const bus = getWidgetBus();

    if (isDefined(bus)) {
      // restore the language saved with the account center
      const userInfoSubscription = bus.subscribe(
        BUS_TOPIC_SGCONNECT_USERINFO,
        (userInfo: { preferred_language: string } | undefined) => {
          if (isDefined(userInfo) && isSupportedLanguage(userInfo.preferred_language)) {
            setLocale(userInfo.preferred_language);
            document.querySelector('html')!.lang = userInfo.preferred_language;
          }
        },
      );

      // sync the language with the account center
      const languageSubscription = bus.subscribe<string>(BUS_TOPIC_GLOBALLANGUAGE, (language) => {
        if (isSupportedLanguage(language)) {
          setLocale(language);
          document.querySelector('html')!.lang = language;
        }
      });

      return () => {
        bus.unsubscribe(userInfoSubscription);
        bus.unsubscribe(languageSubscription);
      };
    }
  }, []);

  return (
    <ReactIntlProvider
      locale={locale}
      // defaultLocale="fr"
      messages={locales[locale]}
      textComponent={Fragment}
    >
      {children}
    </ReactIntlProvider>
  );
};

export const SUPPORTED_LANGUAGES = ['fr', 'en'];

const isSupportedLanguage = (language: string | undefined): language is SupportedLocale =>
  isDefined(language) && SUPPORTED_LANGUAGES.includes(language);
